import React, { useState, useEffect, useCallback } from 'react';
import { Lead } from '../types';
import {
  Activity, Server, Users, RefreshCw, HardDrive,
  ExternalLink, Database, Signal, Network, CheckCircle2,
  Globe, Brain, Zap, Shield, MessageSquare, Scale,
  TrendingUp, Settings, AlertCircle
} from 'lucide-react';
import { N8N_CONFIG, N8N_EDITOR_URL } from '../constants';
import InlineChat from './InlineChat';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface DashboardProps { leads: Lead[]; }

interface ServiceStatus {
  name: string;
  url: string;
  status: 'online' | 'offline' | 'checking';
  latency: number;
  code: number;
  detail?: string;
  icon: React.ReactNode;
  color: string;
}

interface BrainStat {
  collection: string;
  label: string;
  count: number;
  icon: React.ReactNode;
  color: string;
}

const SERVICES_CONFIG = [
  { name: 'CEO Agent (n8n)', url: '/webhook/38cf8499-75bb-4c90-bf0c-52ab4e3c05e6', method: 'POST', body: JSON.stringify({ chatInput: '' }), icon: <Zap className="w-5 h-5" />, color: 'yellow' },
  { name: 'Matrix Homeserver', url: 'https://matrix.arkorbit.at/_matrix/client/versions', method: 'GET', body: undefined, icon: <MessageSquare className="w-5 h-5" />, color: 'blue' },
  { name: 'ARKORBIT BRAIN', url: '/api/qdrant/collections/recht', method: 'GET', body: undefined, icon: <Brain className="w-5 h-5" />, color: 'purple' },
  { name: 'Nextcloud Tresor', url: '/api/nextcloud/status.php', method: 'GET', body: undefined, icon: <HardDrive className="w-5 h-5" />, color: 'emerald' },
  { name: 'Landing Page', url: 'https://arkorbit.at', method: 'GET', body: undefined, icon: <Globe className="w-5 h-5" />, color: 'slate' },
  { name: 'Traefik Gateway', url: '/api/qdrant/', method: 'GET', body: undefined, icon: <Shield className="w-5 h-5" />, color: 'orange' },
];

const BRAIN_COLLECTIONS = [
  { collection: 'recht',    label: 'Recht & OGH',   icon: <Scale className="w-4 h-4" />,    color: 'blue'    },
  { collection: 'sales',    label: 'Sales & Pitches', icon: <TrendingUp className="w-4 h-4" />, color: 'emerald' },
  { collection: 'prozesse', label: 'Prozesse',        icon: <Settings className="w-4 h-4" />, color: 'purple'  },
  { collection: 'markt',    label: 'Markt',           icon: <Globe className="w-4 h-4" />,    color: 'orange'  },
];

const colorMap: Record<string, string> = {
  yellow:  'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
  blue:    'text-blue-400 bg-blue-500/10 border-blue-500/20',
  purple:  'text-purple-400 bg-purple-500/10 border-purple-500/20',
  emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  slate:   'text-slate-300 bg-slate-500/10 border-slate-500/20',
  orange:  'text-orange-400 bg-orange-500/10 border-orange-500/20',
};

export const Dashboard: React.FC<DashboardProps> = ({ leads }) => {
  const [services, setServices] = useState<ServiceStatus[]>(
    SERVICES_CONFIG.map(s => ({ ...s, status: 'checking', latency: 0, code: 0 }))
  );
  const [brainStats, setBrainStats]   = useState<BrainStat[]>(
    BRAIN_COLLECTIONS.map(c => ({ ...c, count: -1 }))
  );
  const [latencyHistory, setLatencyHistory] = useState<{ time: string; latency: number }[]>([]);
  const [lastUpdate, setLastUpdate]   = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  const totalPipeline = leads.reduce((acc, l) => acc + (l.value || 0), 0);
  const activeLeads   = leads.filter(l => l.status !== 'Closed Won').length;

  const checkServices = useCallback(async () => {
    setIsRefreshing(true);
    const results = await Promise.all(
      SERVICES_CONFIG.map(async (svc) => {
        const start = Date.now();
        try {
          const r = await fetch(svc.url, {
            method: svc.method,
            headers: { 'Content-Type': 'application/json' },
            body: svc.body,
            signal: AbortSignal.timeout(5000),
          });
          const latency = Date.now() - start;
          let detail: string | undefined;
          if (r.ok) {
            try {
              const d = await r.json();
              if (d.versions) detail = `v${d.versions.slice(-1)[0]}`;
              else if (d.result?.points_count !== undefined) detail = `${d.result.points_count} Einträge`;
              else if (d.result?.collections) detail = `${d.result.collections.length} Collections`;
            } catch {}
          }
          const ok = r.status >= 200 && r.status < 500;
          return { ...svc, status: ok ? 'online' : 'offline', latency, code: r.status, detail } as ServiceStatus;
        } catch {
          return { ...svc, status: 'offline', latency: 0, code: 0 } as ServiceStatus;
        }
      })
    );
    setServices(results);

    // Latenz-History
    const online = results.filter(r => r.status === 'online');
    if (online.length > 0) {
      const avg = Math.round(online.reduce((a, b) => a + b.latency, 0) / online.length);
      setLatencyHistory(prev => [
        ...prev.slice(-19),
        { time: new Date().toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit', second: '2-digit' }), latency: avg }
      ]);
    }

    setLastUpdate(new Date());
    setIsRefreshing(false);
  }, []);

  const fetchBrainStats = useCallback(async () => {
    const stats = await Promise.all(
      BRAIN_COLLECTIONS.map(async (col) => {
        try {
          const r = await fetch(`/api/qdrant/collections/${col.collection}`);
          if (r.ok) {
            const d = await r.json();
            return { ...col, count: d.result?.points_count ?? 0 };
          }
          return { ...col, count: 0 };
        } catch { return { ...col, count: 0 }; }
      })
    );
    setBrainStats(stats);
  }, []);

  useEffect(() => {
    checkServices();
    fetchBrainStats();
    const interval = setInterval(() => { checkServices(); fetchBrainStats(); }, 15000);
    return () => clearInterval(interval);
  }, [checkServices, fetchBrainStats]);

  const onlineCount  = services.filter(s => s.status === 'online').length;
  const systemHealth = onlineCount === services.length ? 'OPERATIONAL'
    : onlineCount > services.length / 2 ? 'DEGRADED' : 'OFFLINE';
  const statusColor  = systemHealth === 'OPERATIONAL' ? 'text-emerald-400' : systemHealth === 'DEGRADED' ? 'text-amber-400' : 'text-red-400';
  const pulseBg      = systemHealth === 'OPERATIONAL' ? 'bg-emerald-500' : systemHealth === 'DEGRADED' ? 'bg-amber-500' : 'bg-red-500';
  const totalBrain   = brainStats.reduce((a, b) => a + Math.max(b.count, 0), 0);

  return (
    <div className="space-y-6 animate-fade-in pb-10">

      {/* Header */}
      <header className="flex flex-col md:flex-row justify-between items-end pb-4 border-b border-white/5">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className={`h-2.5 w-2.5 rounded-full animate-pulse shadow-[0_0_12px] ${pulseBg} shadow-current`} />
            <span className={`text-xs font-bold tracking-widest uppercase ${statusColor}`}>
              System {systemHealth} · {onlineCount}/{services.length} Services
            </span>
            <span className="text-slate-600 text-xs font-mono">
              {lastUpdate.toLocaleTimeString('de-AT')}
            </span>
          </div>
          <h2 className="text-4xl font-black text-white tracking-tight">CEO COMMAND CENTER</h2>
          <p className="text-slate-400 mt-1 text-sm">Frankfurt Node · Hetzner RTX 4000 Ada · Live</p>
        </div>
        <button
          onClick={() => { checkServices(); fetchBrainStats(); }}
          disabled={isRefreshing}
          className="mt-4 md:mt-0 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-800 text-white px-5 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Prüfe...' : 'Refresh'}
        </button>
      </header>

      {/* KPI Karten */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Pipeline Wert" value={`€ ${totalPipeline.toLocaleString('de-AT')}`} sub="Offene Deals" icon={<TrendingUp className="w-5 h-5 text-emerald-400" />} />
        <KpiCard label="Aktive Leads" value={String(activeLeads)} sub="In Bearbeitung" icon={<Users className="w-5 h-5 text-blue-400" />} />
        <KpiCard label="BRAIN Einträge" value={totalBrain < 0 ? '…' : String(totalBrain)} sub="Gesamt in Qdrant" icon={<Brain className="w-5 h-5 text-purple-400" />} />
        <KpiCard label="Services Online" value={`${onlineCount}/${services.length}`} sub="Frankfurt Node" icon={<Server className="w-5 h-5 text-yellow-400" />} />
      </div>

      {/* Service Health Grid */}
      <div>
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Signal className="w-4 h-4" /> Service Status
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {services.map((svc, i) => (
            <div key={i} className={`p-4 rounded-xl border transition-all ${
              svc.status === 'online'   ? 'bg-slate-900/80 border-slate-700/50 hover:border-slate-600' :
              svc.status === 'checking' ? 'bg-slate-900/50 border-slate-800 animate-pulse' :
                                          'bg-red-950/20 border-red-500/30'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2 rounded-lg border ${colorMap[svc.color]}`}>{svc.icon}</div>
                <div className="text-right">
                  <div className={`text-xs font-bold uppercase ${
                    svc.status === 'online' ? 'text-emerald-400' :
                    svc.status === 'checking' ? 'text-slate-500' : 'text-red-400'
                  }`}>
                    {svc.status === 'checking' ? '…' : svc.status === 'online' ? 'Online' : 'Offline'}
                  </div>
                  {svc.status !== 'checking' && (
                    <div className="text-xs text-slate-600 font-mono">{svc.latency ? `${svc.latency}ms` : `${svc.code}`}</div>
                  )}
                </div>
              </div>
              <p className="text-white font-semibold text-sm">{svc.name}</p>
              {svc.detail && <p className="text-xs text-slate-500 mt-0.5">{svc.detail}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* BRAIN Stats + Latenz Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Latenz Chart */}
        <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
            <Activity className="w-4 h-4 text-blue-400" /> Server Response Time (Live)
          </h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={latencyHistory}>
                <defs>
                  <linearGradient id="latGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}   />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="time" stroke="#475569" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                <YAxis stroke="#475569" tick={{ fontSize: 9 }} unit="ms" width={40} />
                <Tooltip contentStyle={{ backgroundColor: '#0f172a', borderColor: '#1e293b', color: '#fff', fontSize: 11 }} />
                <Area type="monotone" dataKey="latency" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#latGrad)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* BRAIN Collections */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
            <Brain className="w-4 h-4 text-purple-400" /> ARKORBIT BRAIN
          </h3>
          <div className="space-y-3">
            {brainStats.map(stat => (
              <div key={stat.collection} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl">
                <div className={`flex items-center gap-2 ${colorMap[stat.color].split(' ')[0]}`}>
                  {stat.icon}
                  <span className="text-slate-300 text-sm font-medium">{stat.label}</span>
                </div>
                <span className="font-black text-white text-lg">
                  {stat.count < 0 ? '…' : stat.count}
                </span>
              </div>
            ))}
            <div className="pt-2 border-t border-slate-700/50 flex justify-between items-center">
              <span className="text-slate-500 text-xs">Gesamt</span>
              <span className="text-purple-400 font-black">{totalBrain}</span>
            </div>
          </div>
          <a
            href="https://n8n.arkorbit.at"
            target="_blank"
            rel="noreferrer"
            className="mt-4 w-full flex items-center justify-center gap-2 text-xs text-slate-400 hover:text-white transition-colors py-2 border border-slate-700/50 rounded-lg hover:border-slate-500"
          >
            n8n Editor <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>

      {/* Donna — jetzt im eigenen Workspace (Sidebar) */}

      {/* Sales Preview */}
      <div>
        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Globe className="w-4 h-4" /> Sales Agent Preview
        </h3>
        <InlineChat
          title="ARKO – KI-Assistent"
          subtitle="Firmenwissen · BRAIN-powered · Lokal"
          initialMessage="Guten Tag! Ich bin ARKO, Ihr KI-Assistent von ARKORBIT AI Austria. Wie kann ich Ihnen helfen?"
          webhookUrl={N8N_CONFIG.SALES_BOT_WEBHOOK}
          accentColor="blue"
          mode="sales_preview"
        />
      </div>

    </div>
  );
};

const KpiCard: React.FC<{ label: string; value: string; sub: string; icon: React.ReactNode }> = ({ label, value, sub, icon }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
    <div className="flex justify-between items-start mb-3">
      <p className="text-slate-500 text-xs font-bold uppercase tracking-wider">{label}</p>
      {icon}
    </div>
    <p className="text-3xl font-black text-white">{value}</p>
    <p className="text-slate-600 text-xs mt-1">{sub}</p>
  </div>
);

export default Dashboard;
